import { Badge, Box, Card, CardBody, Flex, Heading, HStack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ParticipationRequestStatus,
  type BuyerParticipationRequestDto,
  type EventInvitationDto,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { RecipientGroups } from "./RecipientGroups";

interface Props {
  invitation: EventInvitationDto;
  requests: BuyerParticipationRequestDto[];
  citesFoa?: boolean;
}

// Locked outcome record rendered after the invitation is confirmed (P8).
// Sources recipient groups from persisted selectionOutcome / qualificationVerdict data only —
// never from live SelectionPanel state.
export const InvitationOutcomeRecord = ({ invitation, requests, citesFoa = true }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();

  const qualified = requests.filter((r) => r.status === ParticipationRequestStatus.Qualified);
  const rejected = requests.filter((r) => r.status === ParticipationRequestStatus.Rejected);

  // Invited = QUALIFIED where selectionOutcome is absent (everyone invited when uncapped)
  // or selectionOutcome.selected !== false (explicitly selected in capped flow).
  const invitedEntries = qualified
    .filter((r) => r.selectionOutcome == null || r.selectionOutcome.selected !== false)
    .map((r) => ({ id: r.id, name: r.organizationName ?? r.organizationId }));

  // Not selected = QUALIFIED with selectionOutcome.selected === false (capped flow only).
  const notSelectedEntries = qualified
    .filter((r) => r.selectionOutcome?.selected === false)
    .map((r) => ({
      id: r.id,
      name: r.organizationName ?? r.organizationId,
      reason: r.selectionOutcome?.reason ?? null,
    }));

  // Rejected = REJECTED, reason = rejectionReason (§ 24-10 begrunnelse).
  const rejectedEntries = rejected.map((r) => ({
    id: r.id,
    name: r.organizationName ?? r.organizationId,
    reason: r.rejectionReason ?? null,
  }));

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
      <CardBody>
        <Flex justify="space-between" align="flex-start" gap="3" wrap="wrap" mb="3">
          <Heading as="h3" size="sm" color="smText">
            {t("The invitation was sent {{date}}", {
              date: displayDateWithTime(invitation.confirmedAt),
            })}
          </Heading>
          <Badge colorScheme="gray" fontSize="xs">
            {t("Locked")}
          </Badge>
        </Flex>

        <HStack spacing="3" flexWrap="wrap" mb="1">
          <Text fontSize="sm" color="smMuted">
            {t("Bid deadline")}:{" "}
            <Box as="span" fontWeight="semibold" color="smText">
              {displayDateWithTime(invitation.bidSubmissionDeadline)}
            </Box>
          </Text>
          {invitation.tenderValidityMonths != null && (
            <Text fontSize="sm" color="smMuted">
              {t("Tender validity")}:{" "}
              <Box as="span" color="smText">
                {t("{{count}} months", { count: invitation.tenderValidityMonths })}
              </Box>
            </Text>
          )}
          {invitation.confirmedByName && (
            <Text fontSize="sm" color="smMuted">
              {t("Confirmed by")}:{" "}
              <Box as="span" color="smText">
                {invitation.confirmedByName}
              </Box>
            </Text>
          )}
        </HStack>

        <RecipientGroups
        citesFoa={citesFoa}
          invited={invitedEntries}
          notSelected={notSelectedEntries}
          rejected={rejectedEntries}
        />
      </CardBody>
    </Card>
  );
};
